import React, {useState} from 'react'
import Modal from "./components/dialog/Modal";
import Navigation from "./components/Navigation";
import ProfileDropdownPanel from "./components/ProfileDropdownPanel";
import {withAuthenticationRequired} from "@auth0/auth0-react";
import {Outlet} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faBars} from "@fortawesome/pro-solid-svg-icons";
import {
    faBuilding,
    faAddressBook,
    faFolderOpen,
    faRepeat,
    faFileLines,
    faFileInvoiceDollar,
    faGear,
    faBullhorn,
    faChartLine
} from "@fortawesome/pro-regular-svg-icons";
import Dinero from "dinero.js";
import {IntlProvider, useIntl} from "react-intl";
import enMessages from "./locales/en.json";
import deMessages from './locales/de.json';
import LanguageSelector from './components/navigation/LanguageSelector';
import { ErrorProvider } from './contexts/ErrorContext';
import GlobalErrorDisplay from './components/GlobalErrorDisplay';

type AppProps = {}

const messages = {
    'en-US': enMessages,
    'de-DE': deMessages,
};

const NavigationContent: React.FC<{ sidebarOpen: boolean, setSidebarOpen: (open: boolean) => void, sidebarCollapsed: boolean, setSidebarCollapsed: (collapsed: boolean) => void, locale: string, setLocale: any}> =
    ({sidebarOpen, setSidebarOpen, sidebarCollapsed, setSidebarCollapsed, locale, setLocale}) => {
        const intl = useIntl();

        const navigationItems = [
            {label: "navigation.customers", path: "/customers", icon: faBuilding},
            {label: "navigation.contacts", path: "/contacts", icon: faAddressBook},
            {label: "navigation.marketing", path: "/marketing", icon: faBullhorn},
            {label: "navigation.projects", path: "/projects", icon: faFolderOpen},
            {label: "navigation.subscriptions", path: "/subscriptions", icon: faRepeat},
            {label: "navigation.settlements", path: "/settlements", icon: faFileLines},
            {label: "navigation.invoices", path: "/invoices", icon: faFileInvoiceDollar},
            {label: "navigation.analysis", path: "/analysis", icon: faChartLine},
        ].map(item => ({
            ...item,
            label: intl.formatMessage({id: item.label})
        }));

        const secondaryNavigationItems = [
            {label: "navigation.configuration", path: "/configuration/contracts", icon: faGear},
        ].map(item => ({
            ...item,
            label: intl.formatMessage({id: item.label})
        }))

        return (
            <div className="relative h-screen flex overflow-hidden bg-gray-100">
                <Navigation
                    setSidebarOpen={setSidebarOpen}
                    sidebarOpen={sidebarOpen}
                    sidebarCollapsed={sidebarCollapsed}
                    setSidebarCollapsed={setSidebarCollapsed}
                    navigationItems={navigationItems}
                    secondaryNavigationItems={secondaryNavigationItems}
                />
                <div className="flex-1 overflow-auto focus:outline-hidden">
                    {/* could maybe become an AppHeader component */}
                    <div className="flex shrink-0 relative z-10 bg-gray-100 border-b border-gray-200  shadow-lg">
                        <button type="button" className="px-4 h-16 border-r border-gray-200 text-gray-400 lg:hidden" onClick={() => setSidebarOpen(true)}>
                            <span className="sr-only">Open sidebar</span>
                            <FontAwesomeIcon icon={faBars} className="w-4 h-4" aria-hidden="true"/>
                        </button>
                        <div className="flex-1 px-4 flex justify-end items-center gap-2 sm:px-6 lg:px-8">
                            <LanguageSelector locale={locale} setLocale={setLocale}/>
                            <ProfileDropdownPanel/>
                        </div>
                    </div>
                    <main className="flex-1 relative pb-8 z-0 overflow-y-auto">
                        <Outlet/>
                    </main>
                </div>
                <GlobalErrorDisplay />
            </div>
        );
    };

const App: React.FC<AppProps> = (props) => {
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        const stored = localStorage.getItem('cabana-sidebar-collapsed');
        return stored === 'true';
    })
    const [locale, setLocale] = useState('de-DE');

    const handleSidebarCollapsed = (collapsed: boolean) => {
        setSidebarCollapsed(collapsed);
        localStorage.setItem('cabana-sidebar-collapsed', String(collapsed));
    };

    Dinero.globalLocale = locale
    Dinero.defaultCurrency = 'EUR'

    return (
        <>
            <ErrorProvider>
                <IntlProvider locale={locale} defaultLocale="en-US" messages={messages[locale]}>
                    <NavigationContent sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} sidebarCollapsed={sidebarCollapsed} setSidebarCollapsed={handleSidebarCollapsed} locale={locale} setLocale={setLocale}/>
                    <Modal/>
                </IntlProvider>
            </ErrorProvider>
        </>
    )
}

export default withAuthenticationRequired(App);
